// src/lib/logger.ts
/**
 * Единый логгер для приложения с Sentry Integration
 * CANON v1.0.7: warn/error only, Sentry для errors, structured logs
 * В проде: только warn/error. Никаких console.log/info/debug.
 * В деве: поведение такое же — единообразие между окружениями.
 *
 * Логи не содержат PII/секретов. Пользуйтесь redactor при необходимости.
 */

export interface LogMeta {
  user?: string;
  route?: string;
  rpc?: string;
  duration?: number;
  status?: number;
  [key: string]: string | number | boolean | null | undefined;
}

function serialize(meta?: LogMeta) {
  if (!meta) return '';
  try { return ' ' + JSON.stringify(meta); } catch { return ''; }
}

// Check if Sentry is available (loaded by sentry.init.ts)
function getSentry() {
  try {
    // @ts-expect-error - Sentry may not be loaded
    return window.__SENTRY__;
  } catch {
    return null;
  }
}

function emit(kind: 'warn' | 'error', msg: string, meta?: LogMeta) {
  // Не используем console.* напрямую нигде в коде проекта, только тут.
  // Разрешено Каноном.
  const line = `[${kind.toUpperCase()}] ${msg}${serialize(meta)}`;
  if (kind === 'warn') {

    console.warn(line);
  } else {

    console.error(line);
    // Send errors to Sentry for production monitoring (if available)
    const Sentry = getSentry();
    if (Sentry?.captureException) {
      Sentry.captureException(new Error(msg), { extra: meta });
    }
  }
}

/** Only logs when not in production (VITE_ENV_LABEL !== 'PRD'). Use for debug/info. */
export function devLog(...args: unknown[]) {
  if (import.meta.env.VITE_ENV_LABEL !== 'PRD') {
    console.log(...args);
  }
}

export const logger = Object.freeze({
  warn: (msg: string, meta?: LogMeta) => emit('warn', msg, meta),
  error: (msg: string, meta?: LogMeta) => emit('error', msg, meta),
});